import React, { useState } from "react";
import API from "../../api/axiosInstance";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../css/AddRoom.css";
import AmenitiesList from "./AmenitiesList";
import { ROOM_TYPES } from "../../constants/roomConfig";
import { DoorOpen, Tag, Sparkles, CalendarRange } from "lucide-react";

const pickerDateToKey = (d) => {
  if (!d) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AddRoom = ({ selectedHotel, onAdded, onClose }) => {
  const [form, setForm] = useState({
    hotel_id: selectedHotel?._id ? [selectedHotel._id] : [],
    room_name: "",
    room_type: "",
    price: "",
    no_of_rooms: "",
    bed_count: "1",
    amenities: [],
    from_date: null,
    to_date: null,
  });
  const [submitting, setSubmitting] = useState(false);


  const hotelDisplayName =
    selectedHotel?.global_hotel_id?.global_hotel_name ||
    selectedHotel?.global_hotel_name ||
    "N/A";

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.hotel_id.length) {
      alert("Hotel ID is missing!");
      return;
    }

    const fromKey = pickerDateToKey(form.from_date);
    const toKey = pickerDateToKey(form.to_date);
    if (fromKey && toKey && toKey < fromKey) {
      alert("Available To date cannot be before Available From date.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        bed_count: Math.max(1, parseInt(form.bed_count, 10) || 1),
        from_date: fromKey,
        to_date: toKey,
      };
      await API.post('/ems/rooms', payload);
      alert("Room added successfully!");
      onAdded();
      onClose();
    } catch (err) {
      console.error("Add Room Error:", err);
      alert(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal show d-block"
      style={{ background: "rgba(0,0,0,0.4)" }}
    >
      <div className="modal-dialog modal-dialog-centered modal-md add-room-modal">
        <div className="modal-content">
          <div className="modal-header add-room-modal__header">
            <div className="add-room-modal__header-row">
              <h5 className="modal-title">Add a Room</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
            <p className="add-room-subtitle">
              Add a new room to <strong>{hotelDisplayName}</strong>
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body add-room-body">

              <section className="room-form-section">
                <header className="room-form-section__header">
                  <span className="room-form-section__icon"><DoorOpen size={16} strokeWidth={2} /></span>
                  <h6 className="room-form-section__title">Room Details</h6>
                </header>
                <div className="room-form-grid room-form-grid--2">
                  <div className="room-form-field">
                    <label className="form-label">Room Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      name="room_name"
                      className="form-control"
                      placeholder="Enter room name"
                      value={form.room_name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="room-form-field">
                    <label className="form-label">Room Type <span className="text-danger">*</span></label>
                    <select
                      name="room_type"
                      className="form-control"
                      value={form.room_type}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select room type</option>
                      {ROOM_TYPES.map((rt) => (
                        <option key={rt} value={rt}>{rt}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

              <section className="room-form-section">
                <header className="room-form-section__header">
                  <span className="room-form-section__icon"><Tag size={16} strokeWidth={2} /></span>
                  <h6 className="room-form-section__title">Pricing &amp; Capacity</h6>
                </header>
                <div className="room-form-grid room-form-grid--3">
                  <div className="room-form-field">
                    <label className="form-label">Price Per Night <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      name="price"
                      className="form-control"
                      placeholder="e.g. 100"
                      value={form.price}
                      onChange={handleChange}
                      style={{ MozAppearance: 'textfield' }}
                      onWheel={(e) => e.target.blur()}
                      required
                    />
                  </div>
                  <div className="room-form-field">
                    <label className="form-label">Total Rooms <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      name="no_of_rooms"
                      className="form-control"
                      placeholder="Number of rooms"
                      value={form.no_of_rooms}
                      onChange={handleChange}
                      onWheel={(e) => e.target.blur()}
                      required
                    />
                  </div>
                  <div className="room-form-field">
                    <label className="form-label">Beds Per Room <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      name="bed_count"
                      className="form-control"
                      placeholder="1"
                      min={1}
                      max={10}
                      value={form.bed_count}
                      onChange={handleChange}
                      onWheel={(e) => e.target.blur()}
                      required
                    />
                  </div>
                </div>
              </section>

              <section className="room-form-section">
                <header className="room-form-section__header">
                  <span className="room-form-section__icon"><Sparkles size={16} strokeWidth={2} /></span>
                  <h6 className="room-form-section__title">Amenities</h6>
                </header>
                <AmenitiesList
                  amenities={form.amenities}
                  onChange={(a) => setForm({ ...form, amenities: a })}
                />
              </section>

              <section className="room-form-section">
                <header className="room-form-section__header">
                  <span className="room-form-section__icon"><CalendarRange size={16} strokeWidth={2} /></span>
                  <h6 className="room-form-section__title">Availability</h6>
                </header>
                <div className="room-form-grid room-form-grid--2">
                  <div className="room-form-field">
                    <label className="form-label d-block">Available From <span className="text-danger">*</span></label>
                    <DatePicker
                      selected={form.from_date}
                      onChange={(date) => {
                        const toKey = form.to_date ? pickerDateToKey(form.to_date) : null;
                        const fromKey = date ? pickerDateToKey(date) : null;
                        const clearTo = fromKey && toKey && toKey < fromKey;
                        setForm({
                          ...form,
                          from_date: date,
                          ...(clearTo ? { to_date: null } : {}),
                        });
                      }}
                      className="form-control w-100"
                      dateFormat="dd-MMM-yyyy"
                      placeholderText="Select date"
                      fixedHeight

                      popperProps={{ strategy: "fixed" }}
                      required
                    />
                  </div>
                  <div className="room-form-field">
                    <label className="form-label d-block">Available To <span className="text-danger">*</span></label>
                    <DatePicker
                      selected={form.to_date}
                      onChange={(date) =>
                        setForm({ ...form, to_date: date })
                      }
                      minDate={form.from_date}
                      className="form-control w-100"
                      dateFormat="dd-MMM-yyyy"
                      placeholderText="Select date"
                      fixedHeight

                      popperProps={{ strategy: "fixed" }}
                      required
                    />
                  </div>
                </div>
              </section>

            </div>

            <div className="modal-footer">
              <button type="button" className="room-form-btn room-form-btn--secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="room-form-btn room-form-btn--primary" disabled={submitting}>
                {submitting ? "Adding..." : "Add Room"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddRoom;
